import React, { useState } from 'react';
import { auth as authApi } from '../services/api';

export const LoginPage = ({ onLogin }) => {
  const [view, setView] = useState('login'); // login | forgot
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [devResetUrl, setDevResetUrl] = useState('');

  const resetMessages = () => {
    setError('');
    setInfo('');
    setDevResetUrl('');
  };

  const switchView = (next) => {
    resetMessages();
    setView(next);
  };

  const handleLogin = async () => {
    if (!email || !password) { setError('Email and password are required'); return; }
    setLoading(true);
    resetMessages();
    try {
      await onLogin(email, password);
    } catch (err) {
      setError(err.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Enter your email address');
      return;
    }
    setLoading(true);
    resetMessages();
    try {
      const res = await authApi.forgotPassword(email);
      if (res.emailSent === false && res.devResetUrl) {
        setInfo(res.devNote || 'Email could not be sent. Use the reset link below (development only).');
        setDevResetUrl(res.devResetUrl);
      } else {
        setInfo(res.message || 'If an account exists for that email, password reset instructions have been sent.');
      }
    } catch (err) {
      setError(err.message || 'Could not send reset instructions');
    } finally {
      setLoading(false);
    }
  };

  const isForgot = view === 'forgot';

  return (
    <div className="login-screen">
      <div className="login-card">
        <div className="login-logo">
          <img src="/logo.webp" alt="huhslCap" />
        </div>

        {error && (
          <div style={{ background:'var(--danger-bg)', border:'1px solid var(--danger-border)', color:'var(--danger)', padding:'10px 12px', borderRadius:10, fontSize:12.5, marginBottom:14, textAlign:'center' }}>
            {error}
          </div>
        )}

        {info && (
          <div style={{ background:'var(--success-bg)', border:'1px solid var(--success-border)', color:'var(--success)', padding:'10px 12px', borderRadius:10, fontSize:12.5, marginBottom:14, textAlign:'center' }}>
            {info}
            {devResetUrl && (
              <div style={{ marginTop: 10 }}>
                <a href={devResetUrl} style={{ color: 'var(--accent-dark)', wordBreak: 'break-all', fontWeight: 600 }}>
                  Open password reset link
                </a>
              </div>
            )}
          </div>
        )}

        <div className="login-title">
          {isForgot ? 'Reset your password' : 'Sign in to your account'}
        </div>

        {isForgot && (
          <div className="login-desc">
            Enter your email and we&apos;ll send you a link to choose a new password.
          </div>
        )}

        <div className="login-field">
          <label className="login-label">Email address</label>
          <input
            className="login-input"
            type="email"
            value={email}
            autoComplete="username"
            placeholder="you@company.com"
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (isForgot ? handleForgotPassword() : handleLogin())}
          />
        </div>

        {!isForgot && (
          <div className="login-field">
            <label className="login-label">Password</label>
            <input
              className="login-input"
              type="password"
              value={password}
              autoComplete="current-password"
              placeholder="••••••••"
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
            />
          </div>
        )}

        {!isForgot && (
          <div style={{ textAlign: 'right', marginBottom: 10 }}>
            <button type="button" className="login-link-btn" onClick={() => switchView('forgot')}>
              Forgot password?
            </button>
          </div>
        )}

        <button
          className="btn-login"
          onClick={isForgot ? handleForgotPassword : handleLogin}
          disabled={loading}
          style={{ opacity: loading ? 0.7 : 1 }}
        >
          {loading
            ? (isForgot ? 'Sending…' : 'Signing in…')
            : (isForgot ? 'Send reset link' : 'Sign in')}
        </button>

        {isForgot && (
          <button type="button" className="login-link-btn" onClick={() => switchView('login')} style={{ marginTop: 12 }}>
            Back to sign in
          </button>
        )}

        {!isForgot && (
          <div className="login-hint">Secured with 256-bit TLS · Session expires after 8 hours</div>
        )}
      </div>
    </div>
  );
};
